// Lossless remux: Opus packets out of a WebM (MediaRecorder) capture, into an
// Ogg Opus file (RFC 7845). No re-encode — the packet bytes move unchanged; only
// the container around them is rebuilt. This exists because some Chromium builds
// record only webm/opus, while the protocol's mandatory audio formats are
// audio/mp4 and audio/ogg;codecs=opus.

import {
  EbmlError,
  ID_BLOCK,
  ID_CHANNELS,
  ID_CODEC_ID,
  ID_CODEC_PRIVATE,
  ID_SIMPLE_BLOCK,
  ID_TRACK_ENTRY,
  ID_TRACK_NUMBER,
  readString,
  readUint,
  walkEbml,
} from './ebml';
import { OggPageWriter } from './ogg';
import { packetSamples } from './opus';

export class RemuxError extends Error {}

export interface ParsedWebm {
  codecPrivate: Uint8Array | undefined;
  channels: number;
  packets: Uint8Array[];
}

/** Reads the block's track-number vint and returns the payload after the
 *  4-byte-minimum block header, expanding lacing when present. */
function blockPackets(bytes: Uint8Array, start: number, end: number, opusTrack: number): Uint8Array[] {
  // Track number is an EBML vint (marker stripped).
  const first = bytes[start];
  if (first === undefined) {
    return [];
  }
  let length = 1;
  let mask = 0x80;
  while ((first & mask) === 0) {
    length++;
    mask >>= 1;
    if (length > 8) {
      return [];
    }
  }
  let track = first & (mask - 1);
  for (let i = 1; i < length; i++) {
    track = track * 256 + bytes[start + i]!;
  }
  if (track !== opusTrack) {
    return [];
  }
  let pos = start + length + 2; // skip the 16-bit relative timecode
  const flags = bytes[pos];
  if (flags === undefined) {
    return [];
  }
  pos++;
  const lacingMode = (flags >> 1) & 0x03;
  if (lacingMode === 0) {
    return [bytes.subarray(pos, end)];
  }

  const frameCount = bytes[pos]! + 1;
  pos++;
  const sizes: number[] = [];
  if (lacingMode === 2) {
    // Fixed-size lacing: equal split of the remainder.
    const total = end - pos;
    const each = Math.floor(total / frameCount);
    for (let i = 0; i < frameCount; i++) {
      sizes.push(each);
    }
  } else if (lacingMode === 1) {
    // Xiph lacing: 255-run sizes for all but the last frame.
    for (let i = 0; i < frameCount - 1; i++) {
      let size = 0;
      for (;;) {
        const b = bytes[pos]!;
        pos++;
        size += b;
        if (b !== 255) {
          break;
        }
      }
      sizes.push(size);
    }
    sizes.push(-1); // remainder
  } else {
    // EBML lacing: first size is a plain vint, the rest are signed vint deltas.
    const readVint = (): number => {
      const head = bytes[pos]!;
      let len = 1;
      let m = 0x80;
      while ((head & m) === 0) {
        len++;
        m >>= 1;
      }
      let value = head & (m - 1);
      for (let i = 1; i < len; i++) {
        value = value * 256 + bytes[pos + i]!;
      }
      pos += len;
      return value;
    };
    const readSignedVint = (): number => {
      const head = bytes[pos]!;
      let len = 1;
      let m = 0x80;
      while ((head & m) === 0) {
        len++;
        m >>= 1;
      }
      const raw = readVint();
      // The vint reader above consumed it; bias per spec: 2^(7*len-1) - 1.
      return raw - (2 ** (7 * len - 1) - 1);
    };
    let size = readVint();
    sizes.push(size);
    for (let i = 1; i < frameCount - 1; i++) {
      size += readSignedVint();
      sizes.push(size);
    }
    if (frameCount > 1) {
      sizes.push(-1);
    }
  }

  const packets: Uint8Array[] = [];
  for (const size of sizes) {
    const actual = size === -1 ? end - pos : size;
    packets.push(bytes.subarray(pos, pos + actual));
    pos += actual;
  }
  return packets;
}

/** Exported for the fixture test: the packet list the remux moves unchanged. */
export function parseWebmOpus(bytes: Uint8Array): ParsedWebm {
  let inOpusTrack = false;
  let currentTrackNumber = 0;
  let opusTrack = -1;
  let codecPrivate: Uint8Array | undefined;
  let channels = 1;
  const blockRanges: { start: number; end: number }[] = [];

  walkEbml(bytes, (token) => {
    switch (token.id) {
      case ID_TRACK_ENTRY:
        inOpusTrack = false;
        currentTrackNumber = 0;
        break;
      case ID_TRACK_NUMBER:
        currentTrackNumber = readUint(bytes, token.dataStart, token.dataEnd);
        break;
      case ID_CODEC_ID:
        if (readString(bytes, token.dataStart, token.dataEnd) === 'A_OPUS') {
          inOpusTrack = true;
          opusTrack = currentTrackNumber;
        }
        break;
      case ID_CODEC_PRIVATE:
        if (inOpusTrack) {
          codecPrivate = bytes.subarray(token.dataStart, token.dataEnd);
        }
        break;
      case ID_CHANNELS:
        if (inOpusTrack) {
          channels = readUint(bytes, token.dataStart, token.dataEnd) || 1;
        }
        break;
      case ID_SIMPLE_BLOCK:
      case ID_BLOCK:
        blockRanges.push({ start: token.dataStart, end: token.dataEnd });
        break;
      default:
        break;
    }
  });

  if (opusTrack === -1) {
    throw new RemuxError('no Opus track in the capture');
  }
  const packets: Uint8Array[] = [];
  for (const range of blockRanges) {
    packets.push(...blockPackets(bytes, range.start, range.end, opusTrack));
  }
  // A late TrackNumber (after CodecID) would have left opusTrack at 0; recover by
  // taking every block in that case — a single-track audio capture.
  if (packets.length === 0 && opusTrack === 0) {
    for (const range of blockRanges) {
      packets.push(...blockPackets(bytes, range.start, range.end, currentTrackNumber));
    }
  }
  return { codecPrivate, channels, packets };
}

const OPUS_HEAD_MAGIC = [0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64]; // "OpusHead"

/** WebM's CodecPrivate for A_OPUS IS the OpusHead structure, byte for byte. */
function opusHead(parsed: ParsedWebm): { head: Uint8Array; preSkip: number } {
  const cp = parsed.codecPrivate;
  if (cp !== undefined && cp.length >= 19 && OPUS_HEAD_MAGIC.every((b, i) => cp[i] === b)) {
    const preSkip = cp[10]! | (cp[11]! << 8);
    return { head: cp, preSkip };
  }
  // Synthesize: version 1, channel count from the track, pre-skip 312 (the
  // libopus encoder lookahead), input rate 48000, gain 0, mapping family 0.
  const head = new Uint8Array(19);
  head.set(OPUS_HEAD_MAGIC, 0);
  head[8] = 1;
  head[9] = parsed.channels;
  head[10] = 312 & 0xff;
  head[11] = 312 >> 8;
  new DataView(head.buffer).setUint32(12, 48000, true);
  return { head, preSkip: 312 };
}

function opusTags(): Uint8Array {
  const vendor = new TextEncoder().encode('dialano-webclient');
  const out = new Uint8Array(8 + 4 + vendor.length + 4);
  out.set([0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73], 0); // "OpusTags"
  const view = new DataView(out.buffer);
  view.setUint32(8, vendor.length, true);
  out.set(vendor, 12);
  view.setUint32(12 + vendor.length, 0, true); // zero comments
  return out;
}

/** The remux itself. Throws [RemuxError] / [EbmlError] on anything that is not a
 *  WebM Opus capture — this path only runs when exactly that was recorded. */
export function webmOpusToOgg(bytes: Uint8Array): Uint8Array {
  let parsed: ParsedWebm;
  try {
    parsed = parseWebmOpus(bytes);
  } catch (error) {
    if (error instanceof EbmlError || error instanceof RemuxError) {
      throw new RemuxError(error.message);
    }
    throw error;
  }
  if (parsed.packets.length === 0) {
    throw new RemuxError('the capture carries no audio packets');
  }

  const { head, preSkip } = opusHead(parsed);
  const writer = new OggPageWriter(0x64_69_61_6c); // "dial", stable on purpose
  writer.addPacketPage(head, { granule: 0, bos: true });
  writer.addPacketPage(opusTags(), { granule: 0 });

  let samples = preSkip;
  for (let i = 0; i < parsed.packets.length; i++) {
    const packet = parsed.packets[i]!;
    samples += packetSamples(packet);
    writer.addPacketPage(packet, { granule: samples, eos: i === parsed.packets.length - 1 });
  }
  return writer.finish();
}
